import z from "zod";

import {
  PaginatedRequest,
  PaginatedResponse,
  createPaginatedResponseSchema,
} from "../api/pagination";
import {
  UserProfileAttributes,
  UserProfileAttributesSchema,
} from "../profile/UserProfileAttributes";

import { UserImageMedia, UserImageMediaSchema } from "./image";

import { Interest } from "@/schemas/profile/attributes/UserInterestAttributeSet";
import { parseAsUTC } from "@/utils/datetime";

///////////////////////
// Enums & Constants //
///////////////////////

// categories for recommendations
export enum RecommendationCategory {
  TRENDING = "TRENDING",
  DATING = "DATING",
  FRIENDS = "FRIENDS",
  NAUGHTY = "NAUGHTY",
  WILDCARD = "WILDCARD",
  ONLINE_NOW = "ONLINE_NOW",
  NEW_USERS = "NEW_USERS",
  NEARBY = "NEARBY",
}

// Deprecated Recommendation Categories
export const DeprecatedRecommendationCategories = [
  RecommendationCategory.NAUGHTY,
  RecommendationCategory.WILDCARD,
];

export enum CompatibilityType {
  DATING_FORWARD = "DATING_FORWARD", // A→B dating interest
  DATING_REVERSE = "DATING_REVERSE", // B→A dating interest
  FRIENDS_FORWARD = "FRIENDS_FORWARD", // A→B friendship
  FRIENDS_REVERSE = "FRIENDS_REVERSE", // B→A friendship
}

export enum RecommendationHighlightType {
  COMMON_INTERESTS = "COMMON_INTERESTS", // duplicate of SHARED_INTERESTS
  RECENT_ACTIVITY = "RECENT_ACTIVITY",
  SIMILAR_AGE = "SIMILAR_AGE",
  SHARED_INTERESTS = "SHARED_INTERESTS",
  SHARED_LANGUAGE = "SHARED_LANGUAGE",
  NEARBY = "NEARBY",
  SAME_ETHNICITY = "SAME_ETHNICITY",
  MANY_SHARED_INTERESTS = "MANY_SHARED_INTERESTS",

  // Dating Preferences: Body Type Preference Highlights (broken down)
  BODY_TYPE_FIT = "BODY_TYPE_FIT",
  BODY_TYPE_MUSCULAR = "BODY_TYPE_MUSCULAR",
  BODY_TYPE_TONED = "BODY_TYPE_TONED",
  BODY_TYPE_SLIM = "BODY_TYPE_SLIM",
  BODY_TYPE_AVERAGE = "BODY_TYPE_AVERAGE",
  BODY_TYPE_STOCKY = "BODY_TYPE_STOCKY",
  BODY_TYPE_CHUBBY = "BODY_TYPE_CHUBBY",
  BODY_TYPE_LARGE = "BODY_TYPE_LARGE",

  // Dating Preferences: Role Preference Highlights (broken down)
  ROLE_TOP = "ROLE_TOP",
  ROLE_BOTTOM = "ROLE_BOTTOM",
  ROLE_VERSATILE = "ROLE_VERSATILE",
  ROLE_VERS_TOP = "ROLE_VERS_TOP",
  ROLE_VERS_BOTTOM = "ROLE_VERS_BOTTOM",
  ROLE_SIDE = "ROLE_SIDE",

  // Dating Preferences: Seeking/Looking For Highlights (broken down)
  SEEKING_RELATIONSHIP = "SEEKING_RELATIONSHIP",
  SEEKING_DATES = "SEEKING_DATES",
  SEEKING_CASUAL = "SEEKING_CASUAL",
  SEEKING_ROMANCE = "SEEKING_ROMANCE",
  SEEKING_FRIENDS = "SEEKING_FRIENDS",
  SEEKING_RIGHT_NOW = "SEEKING_RIGHT_NOW",
  SEEKING_OPEN_RELATIONSHIP = "SEEKING_OPEN_RELATIONSHIP",
  SEEKING_POLYAMORY = "SEEKING_POLYAMORY",
  SEEKING_LONG_TERM = "SEEKING_LONG_TERM",
  SEEKING_COFFEE_DATES = "SEEKING_COFFEE_DATES",
}

//////////////////////////
// Core Types & Schemas //
//////////////////////////

// compatibility
export type UserRecommendationCompatibility = {
  rank: number;
  type: CompatibilityType;
  score: number;
};

export const UserRecommendationCompatibilitySchema = z.object({
  rank: z.number().min(0),
  type: z.nativeEnum(CompatibilityType),
  score: z.number().min(0).max(100),
});

// highlight
export type UserRecommendationHighlight = {
  rank: number;
  type: RecommendationHighlightType;
};

export const UserRecommendationHighlightSchema = z.object({
  rank: z.number().min(0),
  type: z.nativeEnum(RecommendationHighlightType),
});

// User recommended item, contains basic user info + matching info
export type UserRecommendedItem = {
  id: string;
  rank: number;
  username: string;
  image: UserImageMedia | null;
  profileAttributes: UserProfileAttributes;
  distance: number | null;
  matchingPercentage: number | null;
  scores: UserRecommendationCompatibility[];
  highlights: UserRecommendationHighlight[];
  commonInterests: Interest[];
  isLocked: boolean;
};

export const UserRecommendedItemSchema = z.object({
  id: z.string().min(1),
  rank: z.number().min(0),
  username: z.string().min(1),
  image: UserImageMediaSchema.nullable(),
  profileAttributes: UserProfileAttributesSchema,
  distance: z.number().nullable(),
  matchingPercentage: z.number().nullable(),
  scores: z.array(UserRecommendationCompatibilitySchema),
  highlights: z.array(UserRecommendationHighlightSchema),
  commonInterests: z.array(z.nativeEnum(Interest)),
  isLocked: z.boolean(),
});

///////////////////////////////
// Paginated Types & Schemas //
///////////////////////////////

export const PaginatedUserRecommendedItemSchema = createPaginatedResponseSchema(
  UserRecommendedItemSchema,
);

///////////////////
// Request Types //
///////////////////

// request body for category specific recommendations
export type CategoryRecommendationRequest = {
  category: RecommendationCategory;
  jobId: string | null;
  page: PaginatedRequest | null;
};

//////////////////////////////
// Response Types & Schemas //
//////////////////////////////

export enum JobStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export type UserRecommendationJobResponse = {
  jobId: string;
  status: JobStatus;
};

export const UserRecommendationJobResponseSchema = z.object({
  jobId: z.string().min(1),
  status: z.nativeEnum(JobStatus),
});

// response body for a specific category recommendations
// used in RecommendationService getCategoryRecommendations
export type UserRecommendedCategoryResponse = {
  jobId: string;
  items: PaginatedResponse<UserRecommendedItem>;
};

export const UserRecommendedCategoryResponseSchema = z.object({
  jobId: z.string().min(1),
  items: PaginatedUserRecommendedItemSchema,
});

/////// For ExploreScreen category feed/preview item ///////
// Used inside UserRecommendedCategoryFeedResponse below
export type RecommendationCategoryFeed = {
  jobId: string;
  category: RecommendationCategory;
  rank: number;
  items: PaginatedResponse<UserRecommendedItem>;
};

// Used inside UserRecommendedCategoryFeedResponseSchema below
export const RecommendationCategoryFeedSchema = z.object({
  jobId: z.string().min(1),
  category: z.nativeEnum(RecommendationCategory),
  rank: z.number().min(0),
  items: PaginatedUserRecommendedItemSchema,
});

// Response body for profile preview recommendations
// Used in RecommendationService getProfilePreviewRecommendations
export type UserRecommendedCategoryFeedResponse = {
  jobId: string;
  nextRefreshTime: Date;
  categories: RecommendationCategoryFeed[];
};

export const UserRecommendedCategoryFeedResponseSchema = z
  .object({
    jobId: z.string().min(1),
    nextRefreshTime: z
      .string()
      .nonempty("Date string cannot be empty") // 2025-10-30T20:27:41.182000+00:00
      .transform(parseAsUTC), // 2025-10-30T20:27:41.182Z
    categories: z.array(RecommendationCategoryFeedSchema).min(1),
  })
  .refine((data) => data.categories.every((cat) => cat.jobId === data.jobId), {
    message: "All category.jobId values must match the top-level jobId",
  });
// .refine(
//   (data) =>
//     data.categories.every(
//       (cat) => Array.isArray(cat.items.items) && cat.items.items.length > 0,
//     ),
//   {
//     message: "Each category must have at least one item",
//     path: ["categories"],
//   },
// );
/////// For ExploreScreen category feed/preview item ///////
